/**
 * TryOn Module
 *
 * Submit virtual try-on jobs and poll until completion.
 * Fires SDK events for UI layers to react to progress and results.
 */

import type {
  TryOnSubmitOptions,
  TryOnJob,
  TryOnResult,
  VideoGenerationOptions,
  VideoGenerationJob,
  TryStyleEvents,
} from '../types';
import type { Fetcher } from '../utils/fetcher';
import type { EventEmitter } from '../utils/emitter';

/** Flat response from POST /api/v1/wrapper/tryon/submit */
interface ApiSubmitResponse {
  success: boolean;
  message?: string;
  // Job-based (async) model — needs polling
  job_id?: string;
  // Both models
  event_id: string;
  correlation_id: string;
  progress: number;
  status?: string;
  // Direct model — already complete on submit
  result_image_url?: string | null;
  /** Array OR comma-separated string (backend inconsistency — normalised by resolveImageUrl) */
  result_image?: string[] | string | null;
  result_gcs_path?: string | null;
  category?: string | null;
  /** Split delivery — front returned immediately, side/back still generating */
  multiview_pending?: boolean;
  multiview_status_endpoint?: string | null;
  multiview_url?: string | null;
  multiview_gcs_path?: string | null;
}

/** Flat response from GET /api/v1/wrapper/tryon/status/:jobId */
interface ApiStatusResponse {
  success: boolean;
  job_id?: string;
  event_id: string;
  progress: number;
  /** Backend reports 'success' or 'completed' when done; 'failed' on error */
  status: string;
  /** Boolean completion flag the backend sets independently of status string */
  completed?: boolean;
  /** Human-readable status; 'Completed' / 'Failed' are terminal values */
  status_text?: string;
  result_image_url?: string | null;
  /** Array OR comma-separated string (backend inconsistency — normalised by resolveImageUrl) */
  result_image?: string[] | string | null;
  result_gcs_path?: string | null;
  error?: string | null;
  category?: string | null;
  /** Split delivery — front returned immediately, side/back still generating */
  multiview_pending?: boolean;
  multiview_status_endpoint?: string | null;
  multiview_url?: string | null;
  multiview_gcs_path?: string | null;
}

/** Flat response from GET /api/v1/wrapper/tryon/events/:eventId/multiview-status */
interface ApiMultiviewStatusResponse {
  multiview_pending: boolean;
  multiview_url?: string | null;
  multiview_gcs_path?: string | null;
  multiview_error?: string | null;
}

/**
 * Resolve the best available image URL from a submit / status response.
 *
 * The backend is inconsistent: result_image_url is a plain string while
 * result_image may arrive as an array or a comma-separated string.  This
 * helper picks the first valid URL across all shapes so callers never have
 * to special-case them.
 */
function resolveImageUrl(
  urlField: string | null | undefined,
  imageField: string[] | string | null | undefined,
): string | null {
  if (urlField) return urlField;

  if (Array.isArray(imageField)) {
    const first = imageField.find(u => typeof u === 'string' && u.startsWith('http'));
    return first ?? null;
  }

  if (typeof imageField === 'string' && imageField) {
    const first = imageField.split(',')[0]?.trim();
    return first && first.startsWith('http') ? first : null;
  }

  return null;
}

export class TryOnModule {
  private readonly pollIntervalMs: number;
  private readonly pollMaxAttempts: number;

  constructor(
    private readonly fetcher: Fetcher,
    private readonly emitter: EventEmitter<TryStyleEvents>,
    pollIntervalMs = 3000,
    pollMaxAttempts = 60,
  ) {
    this.pollIntervalMs = pollIntervalMs;
    this.pollMaxAttempts = pollMaxAttempts;
  }

  /**
   * Submit a virtual try-on job.
   *
   * - **Direct models**: the backend completes the try-on synchronously and returns the
   *   result immediately (`progress: 100`, `status: 'success'`). The returned `TryOnJob`
   *   already contains `result_image_url` — no polling needed.
   * - **Job-based models**: returns `job_id` and `progress: 0`. Use `pollUntilDone()` or
   *   `submitAndWait()` to wait for the result.
   */
  async submit(opts: TryOnSubmitOptions): Promise<TryOnJob> {
    const res = await this.fetcher.request<ApiSubmitResponse>(
      '/api/v1/wrapper/tryon/submit',
      {
        method: 'POST',
        // Split delivery: the front result is returned as soon as it's ready and
        // the side/back view is generated in the background. The SDK then polls
        // `multiview_status_endpoint` for the remaining views.
        headers: { 'X-Split-Delivery': 'true' },
        body: {
          avatarId: opts.avatarId,
          retailerId: opts.retailerId,
          productId: opts.productId,
          ...(opts.category !== undefined && { category: opts.category }),
          ...(opts.model !== undefined && { model: opts.model }),
          ...(opts.productImage !== undefined && { productImageUrl: opts.productImage }),
        },
      },
    );

    const resolvedImageUrl = resolveImageUrl(res.result_image_url, res.result_image);
    const imageArray = Array.isArray(res.result_image) ? res.result_image : null;

    const job: TryOnJob = {
      job_id:          res.job_id,
      event_id:        res.event_id,
      correlation_id:  res.correlation_id,
      progress:        res.progress,
      status:          (res.status as TryOnJob['status']) ?? (res.job_id ? 'processing' : 'pending'),
      result_image_url: resolvedImageUrl,
      result_image:    imageArray ?? undefined,
      result_gcs_path: res.result_gcs_path ?? null,
      category:        res.category ?? null,
      multiview_pending: res.multiview_pending,
      multiview_status_endpoint: res.multiview_status_endpoint ?? null,
      multiview_url:   res.multiview_url ?? null,
      multiview_gcs_path: res.multiview_gcs_path ?? null,
    };

    this.emitter.emit('tryon:submitted', job);

    // Direct model completed on submit — emit complete immediately (front view).
    if ((res.status === 'success' || res.progress === 100) && resolvedImageUrl) {
      const result: TryOnResult = {
        job_id:          res.job_id,
        event_id:        res.event_id,
        status:          'success',
        progress:        100,
        result_image_url: resolvedImageUrl,
        result_image:    imageArray,
        result_gcs_path: res.result_gcs_path ?? null,
        completedAt:     new Date().toISOString(),
        category:        res.category ?? null,
        multiview_pending: res.multiview_pending,
        multiview_status_endpoint: res.multiview_status_endpoint ?? null,
        multiview_url:   res.multiview_url ?? null,
        multiview_gcs_path: res.multiview_gcs_path ?? null,
      };
      this.emitter.emit('tryon:complete', result);

      // Split delivery — side/back still generating: surface the pending signal so
      // UI layers can show the front result immediately and poll for the rest.
      if (res.multiview_pending && res.multiview_status_endpoint) {
        this.emitter.emit('tryon:multiview-pending', {
          event_id: res.event_id,
          status_endpoint: res.multiview_status_endpoint,
        });
      }
    }

    return job;
  }

  /**
   * Get the current status of a try-on job.
   */
  async getStatus(jobId: string): Promise<TryOnResult> {
    const res = await this.fetcher.request<ApiStatusResponse>(
      `/api/v1/wrapper/tryon/status/${jobId}`,
    );

    const resolvedImageUrl = resolveImageUrl(res.result_image_url, res.result_image);
    const imageArray = Array.isArray(res.result_image) ? res.result_image : null;

    // The backend signals completion via multiple independent fields — check all of them.
    const isDone =
      res.status === 'success' ||
      res.status === 'completed' ||
      res.completed === true ||
      (typeof res.status_text === 'string' && res.status_text.toLowerCase() === 'completed') ||
      (res.progress >= 100 && !!resolvedImageUrl);

    const isFailed =
      res.status === 'failed' ||
      res.status === 'error' ||
      (typeof res.status_text === 'string' && res.status_text.toLowerCase() === 'failed');

    const normalizedStatus: TryOnResult['status'] = isFailed
      ? 'error'
      : isDone
        ? 'completed'
        : (res.status as TryOnResult['status']);

    return {
      job_id:          res.job_id ?? jobId,
      event_id:        res.event_id,
      status:          normalizedStatus,
      progress:        res.progress,
      result_image_url: resolvedImageUrl,
      result_image:    imageArray,
      result_gcs_path: res.result_gcs_path ?? null,
      completedAt:     isDone ? new Date().toISOString() : null,
      error:           res.error ?? null,
      category:        res.category ?? null,
      multiview_pending: res.multiview_pending,
      multiview_status_endpoint: res.multiview_status_endpoint ?? null,
      multiview_url:   res.multiview_url ?? null,
      multiview_gcs_path: res.multiview_gcs_path ?? null,
    };
  }

  /**
   * Get the status of the side/back view for a split-delivery try-on event.
   * Returns `multiview_pending: true` while still generating, then the signed
   * `multiview_url` once ready (or `multiview_error` on failure).
   */
  async getMultiviewStatus(eventId: string): Promise<{
    multiview_pending: boolean;
    multiview_url: string | null;
    multiview_gcs_path: string | null;
    multiview_error: string | null;
  }> {
    const res = await this.fetcher.request<ApiMultiviewStatusResponse>(
      `/api/v1/wrapper/tryon/events/${encodeURIComponent(eventId)}/multiview-status`,
    );
    return {
      multiview_pending: res.multiview_pending === true,
      multiview_url: res.multiview_url ?? null,
      multiview_gcs_path: res.multiview_gcs_path ?? null,
      multiview_error: res.multiview_error ?? null,
    };
  }

  /**
   * Poll the split-delivery multiview status until the side/back view is ready,
   * failed, or the timeout is reached. Emits `tryon:multiview-ready` /
   * `tryon:multiview-error` once a terminal state is reached.
   */
  async pollMultiviewUntilDone(
    eventId: string,
    pollIntervalMs = 2000,
    pollMaxAttempts = 30,
  ): Promise<{
    multiview_pending: boolean;
    multiview_url: string | null;
    multiview_gcs_path: string | null;
    multiview_error: string | null;
  }> {
    for (let attempt = 0; attempt < pollMaxAttempts; attempt++) {
      const status = await this.getMultiviewStatus(eventId);

      if (!status.multiview_pending) {
        if (status.multiview_url) {
          this.emitter.emit('tryon:multiview-ready', {
            event_id: eventId,
            url: status.multiview_url,
            gcs_path: status.multiview_gcs_path,
          });
        } else {
          this.emitter.emit('tryon:multiview-error', {
            event_id: eventId,
            error: status.multiview_error ?? 'Side/back view generation failed.',
          });
        }
        return status;
      }

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    const errMsg = `Side/back view did not complete within the timeout period.`;
    this.emitter.emit('tryon:multiview-error', { event_id: eventId, error: errMsg });
    return {
      multiview_pending: true,
      multiview_url: null,
      multiview_gcs_path: null,
      multiview_error: errMsg,
    };
  }

  /**
   * Poll the job status until it reaches `completed` or `error`, then return the result.
   * Fires `tryon:progress`, `tryon:complete`, and `tryon:error` events during the process.
   *
   * @param jobId - The job_id from the submit response
   */
  async pollUntilDone(jobId: string): Promise<TryOnResult> {
    let attempts = 0;

    return new Promise<TryOnResult>((resolve, reject) => {
      const poll = async () => {
        attempts++;

        if (attempts > this.pollMaxAttempts) {
          const err = new Error(`Try-on job ${jobId} did not complete within the timeout period`);
          this.emitter.emit('tryon:error', { job_id: jobId, error: err.message });
          reject(err);
          return;
        }

        try {
          const result = await this.getStatus(jobId);

          this.emitter.emit('tryon:progress', {
            job_id: result.job_id,
            progress: result.progress,
            status: result.status,
          });

          if (result.status === 'completed') {
            this.emitter.emit('tryon:complete', result);
            resolve(result);
            return;
          }

          if (result.status === 'error') {
            const errMsg = result.error ?? 'Try-on processing failed';
            this.emitter.emit('tryon:error', { job_id: result.job_id, error: errMsg });
            reject(new Error(errMsg));
            return;
          }

          // Still processing — schedule next poll
          setTimeout(poll, this.pollIntervalMs);
        } catch (e) {
          reject(e);
        }
      };

      void poll();
    });
  }

  /**
   * One-shot helper: submit a try-on AND wait for the result.
   *
   * - If the backend completed synchronously (direct model), returns immediately.
   * - If a `job_id` was returned (async model), polls until done.
   *
   * @returns The completed TryOnResult with result_image_url
   */
  async submitAndWait(opts: TryOnSubmitOptions): Promise<TryOnResult> {
    const job = await this.submit(opts);

    // Direct model — already done, return immediately
    if ((job.status === 'success' || job.progress === 100) && job.result_image_url) {
      return {
        job_id:          job.job_id,
        event_id:        job.event_id,
        status:          'completed',
        progress:        100,
        result_image_url: job.result_image_url,
        result_image:    job.result_image ?? null,
        result_gcs_path: job.result_gcs_path ?? null,
        completedAt:     new Date().toISOString(),
        error:           null,
        category:        job.category ?? null,
        multiview_pending: job.multiview_pending,
        multiview_status_endpoint: job.multiview_status_endpoint ?? null,
        multiview_url:   job.multiview_url ?? null,
        multiview_gcs_path: job.multiview_gcs_path ?? null,
      };
    }
    // Job-based async model — must poll
    if (!job.job_id) {
      throw new Error(
        `Try-on submit did not return a job_id or an immediate result. ` +
        `event_id=${job.event_id}`,
      );
    }

    return this.pollUntilDone(job.job_id);
  }

  /**
   * Download try-on result image bytes through the API (avoids browser CORS when saving
   * GCS signed URLs with an anchor `download` attribute).
   */
  async downloadResultImageBlob(params: {
    eventId: string;
    retailerId: string;
    /** Carousel index: 0 = primary result, 1 = multiview side when present */
    index?: number;
  }): Promise<Blob> {
    const q = new URLSearchParams({ retailerId: params.retailerId });
    if (params.index != null) {
      q.set('index', String(params.index));
    }
    return this.fetcher.requestBlob(
      `/api/v1/wrapper/tryon/events/${encodeURIComponent(params.eventId)}/result-image?${q}`,
    );
  }

  /**
   * Generate a video from a completed try-on result (paid users only).
   *
   * @param opts - Video generation options including tryonJobId and resultImageUrl
   * @returns VideoGenerationJob with video_job_id for polling
   */
  async generateVideo(opts: VideoGenerationOptions): Promise<VideoGenerationJob> {
    if (!opts.resultGcsPath) {
      throw new Error('Video generation requires a resultGcsPath from the try-on result.');
    }

    const res = await this.fetcher.request<{
      success: boolean;
      video_url?: string | null;
      gcs_path?: string | null;
      status?: string;
      progress?: number;
      video_job_id?: string;
      createdAt?: string;
    }>(
      '/api/v1/direct-tryon/generate-video',
      {
        method: 'POST',
        body: {
          resultGcsPath: opts.resultGcsPath,
          eventId: opts.tryonEventId,
          avatarId: opts.avatarId,
          productId: opts.productId,
        },
      },
    );

    return {
      video_job_id: res.video_job_id ?? null,
      tryonJobId: opts.tryonJobId ?? null,
      tryonEventId: opts.tryonEventId ?? null,
      status: (res.status as VideoGenerationJob['status']) ?? (res.video_url ? 'completed' : 'pending'),
      progress: res.progress ?? (res.video_url ? 100 : 0),
      video_url: res.video_url ?? null,
      gcs_path: res.gcs_path ?? null,
      createdAt: res.createdAt ?? new Date().toISOString(),
    };
  }

  /**
   * Poll the video generation job status until it completes.
   *
   * @param videoJobId - The video_job_id from generateVideo response
   * @returns VideoGenerationJob with video_url when complete
   */
  async getVideoStatus(videoJobId: string): Promise<VideoGenerationJob> {
    const res = await this.fetcher.request<{
      success: boolean;
      video_job_id: string;
      tryonJobId: string;
      status: string;
      progress: number;
      video_url?: string | null;
      createdAt: string;
    }>(`/api/v1/wrapper/tryon/video/status/${videoJobId}`);

    return {
      video_job_id: res.video_job_id,
      tryonJobId: res.tryonJobId,
      status: (res.status as VideoGenerationJob['status']) ?? 'processing',
      progress: res.progress,
      video_url: res.video_url ?? null,
      createdAt: res.createdAt,
    };
  }

  /**
   * Poll video generation until completion.
   *
   * @param videoJobId - The video_job_id from generateVideo
   * @returns Completed VideoGenerationJob with video_url
   */
  async pollVideoUntilDone(videoJobId: string): Promise<VideoGenerationJob> {
    let attempts = 0;

    return new Promise<VideoGenerationJob>((resolve, reject) => {
      const poll = async () => {
        attempts++;

        if (attempts > this.pollMaxAttempts) {
          const err = new Error(
            `Video generation job ${videoJobId} did not complete within the timeout period`
          );
          reject(err);
          return;
        }

        try {
          const result = await this.getVideoStatus(videoJobId);

          if (result.status === 'completed' && result.video_url) {
            resolve(result);
            return;
          }

          if (result.status === 'failed') {
            reject(new Error(`Video generation failed for job ${videoJobId}`));
            return;
          }

          // Still processing — schedule next poll
          setTimeout(poll, this.pollIntervalMs);
        } catch (e) {
          reject(e);
        }
      };

      void poll();
    });
  }

  /**
   * One-shot helper: generate video and wait for completion.
   *
   * @param opts - Video generation options
   * @returns Completed VideoGenerationJob with video_url
   */
  async generateVideoAndWait(opts: VideoGenerationOptions): Promise<VideoGenerationJob> {
    const job = await this.generateVideo(opts);
    if (job.video_url || job.status === 'completed') {
      return job;
    }
    if (!job.video_job_id) {
      throw new Error('Video generation did not return a job ID for polling.');
    }
    return this.pollVideoUntilDone(job.video_job_id);
  }
}
